import {
  Breakpoints,
  CTA,
  styled,
  Text,
  TextTag,
  ThemeProvider,
  TokenTextAppearance,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import React, { useCallback, useContext } from 'react';
import {FindCar} from '@volkswagen-onehub/icons-core';
import { useFeatureServices } from '../../use-feature-services';
import { FeatureAppConfigContext } from '../../use-feature-app-configs';
import { getLang } from '../../trans';
import { SelectedAcodesContext } from '../../context/SelectedAcodes';
import { TrackingEvents } from '../../types/enums/tracking-events.enum';
import { addNbsp } from '../../utils/addNbsp';
import { useFeatureAppContent } from '../../use-feature-app-content';

export const BannerWrapper = styled.div`
  width: 100%;
  background-image: ${(props) => props.theme.colors.gradient.radial};
  overflow: hidden;
`;

const BannerContent = styled.div`
  --container-0-grid002-560-grid004: ${(props) => props.theme.size.grid002};
  @media (min-width: ${Breakpoints.b560}px) {
    --container-0-grid002-560-grid004: ${(props) => props.theme.size.grid004};
  }
  margin: 0 auto;
  width: 100%;
  max-width: 2560px;
  padding: ${(props) => props.theme.size.dynamic0150} var(--container-0-grid002-560-grid004) ${(props) => props.theme.size.dynamic0250};
  display: grid;
  column-gap: calc(${(props) => props.theme.size.dynamic0100});
  row-gap: calc(${(props) => props.theme.size.dynamic0050});
  @media (min-width: 1025px) {
    position: relative;
    padding: 0;
  }
`;

const CompetitorsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: ${(props) => props.theme.size.dynamic0100};
  justify-content: flex-start;
  color: ${(props) => props.theme.colors.content.primary};

  @media screen and (min-width: 1025px) {
    padding: ${(props) => props.theme.size.dynamic0100} 250px;
    align-items: center;
    justify-content: center;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${(props) => props.theme.size.dynamic0050};
  @media screen and (min-width: 1025px) {
    align-items: center;
    text-align: center;
  }
`;

const IconContainer = styled.div`
  flex: 0 0 auto;
  align-self: center;
  display: flex;
  align-items: center;
  svg {
    width: 150px;
    height: 150px;
    color: ${(props) => props.theme.colors.content.primary};
  }
  @media screen and (min-width: 1025px) {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 200px;
    height: 200px;
    svg {
      width: 200px;
      height: 200px;
      transform: translateY(3%) scale(1.25);
    }
  }
`;

const StyledButton = styled(CTA)`
  border-radius: 500px;
`;

export default () => {
  const lang = getLang();

  const { tracking } = useFeatureServices();
  const { banner, header, subhead, callToAction, callToActionEN } = useFeatureAppContent();

  const { compareToolUrl } = useContext(FeatureAppConfigContext);
  const { acodes } = useContext(SelectedAcodesContext);

  const baseUrl = compareToolUrl[lang];

  const getCompareHref = useCallback(() => {
    return `${baseUrl}?acodes=${acodes.join(",")}`;
  }, [acodes]);

  if (banner === "hide" || !header?.trim().length || !callToAction?.trim().length) {
    return null;
  }

  const handleClick = (e: React.SyntheticEvent<HTMLAnchorElement>) => {
    tracking.track(TrackingEvents.BUTTON_CLICK, {
      eventInfo: {
        eventAction: TrackingEvents.BUTTON_CLICK,
        contentId: "compare full specs"
      },
      environment: {
        featureAppId: 'CompareDimensionsWidget-D6-CA',
        featureAppIdAppVersion: '1.0',
      },
      personalization: {
        status: false,
      },
      link: {
        name: callToActionEN as string,
        url: e.currentTarget.href
      }
    });
  };

  return (
    <ThemeProvider theme="inverted">
      <BannerWrapper>
        <BannerContent>
        <IconContainer>
          <FindCar aria-hidden="true" />
        </IconContainer>
        <CompetitorsContainer>
          <TitleContainer>
            <Text
              tag={TextTag.h2}
              color={TokenTextColor.inherit}
              appearance={TokenTextAppearance.headline400}
              bold
            >
              {addNbsp(header)}
            </Text>
            {
              subhead
              ? <Text
                tag={TextTag.p}
                color={TokenTextColor.inherit}
                appearance={TokenTextAppearance.copy200}
              >
                {addNbsp(subhead)}
              </Text>
              : null
            }
          </TitleContainer>
          <StyledButton
            tag="a"
            emphasis="primary"
            href={getCompareHref()}
            onClick={handleClick}
            size="large"
            target="_self"
          >
            {callToAction}
          </StyledButton>
        </CompetitorsContainer>
        </BannerContent>
      </BannerWrapper>
    </ThemeProvider>
  );
};
